import { Injectable, inject } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { environment } from "src/environments/environment";
import { SessionService } from "../../services/session.service";
import { WEBSOCKET_EVENT_TYPES } from "../constants/websocket-event-types.constants";

export interface WebSocketMessage {
  type: string;
  [key: string]: any;
}

export interface ExportCompleteMessage extends WebSocketMessage {
  type: typeof WEBSOCKET_EVENT_TYPES.EXPORT_COMPLETE;
  jobId: string;
  reportType: string;
  message: string;
}

@Injectable({
  providedIn: "root",
})
export class WebSocketService {
  private socket: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectInterval = 1000; // Start with 1 second
  private readonly baseReconnectInterval = 1000;
  private referenceCount = 0; // Reference counter
  /** When true (dev only), do not open or auto-reconnect the socket. */
  private devConnectionSuppressed = false;

  // Observables for connection state
  private connectionStatus$ = new BehaviorSubject<boolean>(false);
  private messages$ = new Subject<WebSocketMessage>();

  // WebSocket URL
  private readonly wsUrl = environment.websocketUrl;

  // SessionService injection
  private sessionService = inject(SessionService);

  constructor() {}
  /**
   * Connect to the WebSocket with a reference counter
   */
  async connect(): Promise<void> {
    this.referenceCount++;

    if (this.socket?.readyState === WebSocket.OPEN) {
      return; // Already connected
    }

    if (this.isDevConnectionSuppressed()) {
      this.connectionStatus$.next(false);
      return;
    }

    await this.establishConnection();
  }

  /** Dev-only: disconnect and block auto-reconnect until re-enabled. */
  disableDevConnection(): void {
    if (!environment.devWebSocketToggleEnabled) {
      return;
    }
    this.devConnectionSuppressed = true;
    this.forceDisconnect();
  }

  /** Dev-only: allow connection and open the socket if the app holds a reference. */
  async enableDevConnection(): Promise<void> {
    if (!environment.devWebSocketToggleEnabled) {
      return;
    }
    this.devConnectionSuppressed = false;
    this.reconnectAttempts = 0;
    this.reconnectInterval = this.baseReconnectInterval;

    if (this.referenceCount === 0) {
      this.referenceCount = 1;
    }

    if (this.socket?.readyState === WebSocket.OPEN) {
      this.connectionStatus$.next(true);
      return;
    }

    await this.establishConnection();
  }

  isDevConnectionSuppressed(): boolean {
    return (
      environment.devWebSocketToggleEnabled && this.devConnectionSuppressed
    );
  }

  isDevWebSocketToggleAvailable(): boolean {
    return environment.devWebSocketToggleEnabled;
  }

  private async establishConnection(): Promise<void> {
    try {
      const token = await this.sessionService.getToken();

      if (!token) {
        console.error("No token available for WebSocket connection");
        return;
      }

      const wsUrlWithToken = `${this.wsUrl}?Authorization=${encodeURIComponent(token)}`;
      this.socket = new WebSocket(wsUrlWithToken);
      this.setupEventListeners();
    } catch (error) {
      console.error("Error connecting to WebSocket:", error);
      this.scheduleReconnect();
    }
  }

  /**
   * Disconnect from the WebSocket with a reference counter
   */
  disconnect(): void {
    if (this.referenceCount > 0) {
      this.referenceCount--;
    }

    // Only disconnect when there are no remaining references
    if (this.referenceCount === 0 && this.socket) {
      this.socket.close(1000, "No more references"); // Code 1000 = intentional close
      this.socket = null;
      this.connectionStatus$.next(false);
    }
  }

  /**
   * Force disconnect (useful for logout)
   */
  forceDisconnect(): void {
    this.referenceCount = 0;
    if (this.socket) {
      this.socket.close(1000, "Intentional disconnect"); // Code 1000 = intentional close
      this.socket = null;
    }
    this.connectionStatus$.next(false);
  }

  /**
   * Get the connection status
   */
  getConnectionStatus(): Observable<boolean> {
    return this.connectionStatus$.asObservable();
  }

  /**
   * Get received messages
   */
  getMessages(): Observable<WebSocketMessage> {
    return this.messages$.asObservable();
  }

  /**
   * Set up WebSocket event listeners
   */
  private setupEventListeners(): void {
    if (!this.socket) return;

    this.socket.onopen = () => {
      this.connectionStatus$.next(true);
      this.reconnectAttempts = 0;
      this.reconnectInterval = this.baseReconnectInterval;
    };

    this.socket.onmessage = (event) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);
        this.messages$.next(message);
      } catch (error) {
        console.error("Error parsing WebSocket message:", error);
      }
    };

    this.socket.onclose = (event) => {
      this.connectionStatus$.next(false);

      // Only reconnect if it was not an intentional close
      if (event.code !== 1000) {
        this.scheduleReconnect();
      }
    };

    this.socket.onerror = (error) => {
      console.error("WebSocket error:", error);
      this.connectionStatus$.next(false);
    };
  }

  /**
   * Schedule automatic reconnection
   */
  private scheduleReconnect(): void {
    if (this.isDevConnectionSuppressed()) {
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error("Max reconnect attempts reached");
      return;
    }

    setTimeout(async () => {
      this.reconnectAttempts++;

      await this.connect();

      // Increase the reconnect interval exponentially
      this.reconnectInterval = Math.min(this.reconnectInterval * 2, 30000);
    }, this.reconnectInterval);
  }

  /**
   * Reconnect manually
   */
  async reconnect(): Promise<void> {
    if (this.isDevConnectionSuppressed()) {
      return;
    }

    this.disconnect();
    this.reconnectAttempts = 0;
    this.reconnectInterval = this.baseReconnectInterval;
    await this.connect();
  }
}
